import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  Heading,
  HStack,
  ButtonGroup,
  Tooltip,
  IconButton,
} from "@chakra-ui/react";
import styles from "../Ace.module.css";
import { connect } from "react-redux";
import { Resize1, Resize2 } from "../icons";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";

const EditCorrections = (props) => {
  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (name) => {
    setOpenGroups((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  // Navigate to suggestion position
  const moveToPosition = (item) => {
    const editor = props.ace.current.editor;

    editor.session.selection.setRange({
      start: {
        row: item.startAnchor.row,
        column: item.startAnchor.column
      },
      end: {
        row: item.endAnchor.row,
        column: item.endAnchor.column
      },
    });

    editor.scrollToLine(item.startAnchor.row, true, true);
    editor.focus();
  };

  // Insert query at the beginning of the line
  const insertQuery = (item) => {
    console.log("Inserting query for:", item);

    if (!item || !item.startAnchor) {
      console.error("Invalid item:", item);
      return;
    }

    const editor = props.ace.current.editor;
    const session = editor.session;

    try {
      const row = item.startAnchor.row;
      const styleName = item.styleInfo?.name || "Query";
      const queryText = `\\AQ{${styleName}}\n`;

      // Insert query at the beginning of the line
      session.insert({ row, column: 0 }, queryText);

      // Move cursor to the inserted query
      editor.gotoLine(row + 1, 0);
      editor.scrollToLine(row, true, true);

      // Update text file
      if (props.setTextFile) {
        props.setTextFile(session.getValue());
      }

      console.log(`Query inserted: \\AQ{${styleName}}`);
    } catch (error) {
      console.error("Error inserting query:", error);
    }
  };

  // Accept single suggestion
  const acceptSuggestion = (item) => {
    console.log("Accepting suggestion:", item);

    if (!item || !item.startAnchor || !item.endAnchor) {
      console.error("Invalid item:", item);
      return;
    }

    const editor = props.ace.current.editor;
    const session = editor.session;
    const Range = window.ace.require('ace/range').Range;

    try {
      // Create range from anchor positions
      const range = new Range(
        item.startAnchor.row,
        item.startAnchor.column,
        item.endAnchor.row,
        item.endAnchor.column
      );

      // Replace the text
      session.replace(range, item.suggestionText);

      // Remove marker
      if (item.markerId) {
        session.removeMarker(item.markerId);
      }

      // Detach anchors
      if (item.startAnchor) item.startAnchor.detach();
      if (item.endAnchor) item.endAnchor.detach();

      // Update text file
      if (props.setTextFile) {
        props.setTextFile(session.getValue());
      }

      // Remove from Redux store
      props.removeAutoSuggestion(item);

      console.log(`Accepted: ${item.originalText} → ${item.suggestionText}`);
    } catch (error) {
      console.error("Error accepting suggestion:", error);
    }
  };

  // Reject single suggestion
  const rejectSuggestion = (item) => {
    console.log("Rejecting suggestion:", item);

    if (!item || !item.startAnchor) {
      console.error("Invalid item:", item);
      return;
    }

    const editor = props.ace.current.editor;
    const session = editor.session;

    try {
      // Remove marker
      if (item.markerId) {
        session.removeMarker(item.markerId);
      }

      // Detach anchors
      if (item.startAnchor) item.startAnchor.detach();
      if (item.endAnchor) item.endAnchor.detach();

      // Add to rejected list
      props.addAutoSuggestionReject({
        row: item.startAnchor.row,
        column: item.startAnchor.column,
        originalText: item.originalText,
        styleName: item.styleInfo?.name
      });

      // Remove from suggestions
      props.removeAutoSuggestion(item);

      console.log(`Rejected: ${item.originalText}`);
    } catch (error) {
      console.error("Error rejecting suggestion:", error);
    }
  };

  // Accept all suggestions in a group
  const acceptAllSuggestions = (items) => {
    console.log("Accepting all suggestions:", items.length);

    // Process in reverse order to avoid index issues
    [...items].reverse().forEach(item => {
      acceptSuggestion(item);
    });
  };

  // Reject all suggestions in a group
  const rejectAllSuggestions = (items) => {
    console.log("Rejecting all suggestions:", items.length);

    // Process in reverse order to avoid index issues
    [...items].reverse().forEach(item => {
      rejectSuggestion(item);
    });
  };

  // Clear all suggestions and rejections
  const handleClearAll = () => {
    const editor = props.ace.current.editor;
    const session = editor.session;

    // Enable editor for editing
    props.editToolAction({});
    props.toolStyles({ key: null, data: [], isChecked: false });
    props.editToolRejected();

    // Remove all markers and detach all anchors
    props.autoSuggestions.forEach(item => {
      if (item.markerId) {
        session.removeMarker(item.markerId);
      }
      if (item.startAnchor) item.startAnchor.detach();
      if (item.endAnchor) item.endAnchor.detach();
    });

    // Clear Redux state
    props.clearAutoSuggestions();
    props.clearAutoSuggestionRejects();
  };

  // Filter out rejected suggestions
  const filteredSuggestions = useMemo(() => {
    const suggestions = props.autoSuggestions || [];
    const rejected = props.autoSuggestionsRejected || [];

    return suggestions.filter(
      (item) => !rejected.some(
        (rejItem) =>
          rejItem.row === item.startAnchor.row &&
          rejItem.column === item.startAnchor.column
      )
    );
  }, [props.autoSuggestions, props.autoSuggestionsRejected]);

  // Group suggestions by style name
  const groupedSuggestions = useMemo(() => {
    return filteredSuggestions.reduce((acc, item) => {
      const styleName = item.styleInfo.name;
      if (!acc[styleName]) {
        acc[styleName] = [];
      }
      acc[styleName].push(item);
      return acc;
    }, {});
  }, [filteredSuggestions]);

  return (
    <>
      <div className={styles.header}>
        <HStack
          backgroundColor="var(--primary-main)"
          justifyContent="space-between"
          h="48px"
          width="auto"
          zIndex={10}
        >
          <ButtonGroup spacing="1">
            <Button
              color="white"
              size="lg"
              colorScheme="#f47c0d"
              aria-label="Suggestions"
            >
              Suggestions ({filteredSuggestions.length})
            </Button>

            <Box display="flex" position={"absolute"} right={0}>
              <Button
                onClick={handleClearAll}
                color="white"
                size="lg"
                colorScheme="#f47c0d"
                aria-label="Clear All"
                _hover={{ bg: "var(--secondary-main)" }}
              >
                Clear All
              </Button>
              {props.pdfFull ? (
                <Tooltip label="Minimize">
                  <IconButton
                    onClick={props.toggleEditor}
                    size="lg"
                    bg="var(--primary-main)"
                    aria-label="Resize"
                    icon={<Resize2 />}
                    _hover={{ bg: "var(--secondary-main)" }}
                  />
                </Tooltip>
              ) : (
                <Tooltip label="Maximize">
                  <IconButton
                    onClick={props.toggleEditor}
                    size="lg"
                    colorScheme="#f47c0d"
                    aria-label="Resize"
                    icon={<Resize1 />}
                    bg="var(--primary-main)"
                    _hover={{ bg: "var(--secondary-main)" }}
                  />
                </Tooltip>
              )}
            </Box>
          </ButtonGroup>
        </HStack>
      </div>

      <VStack
        overflowY="auto"
        overflowX="hidden"
        height="84vh"
        gap="0"
        css={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        }}
      >
        {Object.keys(groupedSuggestions).length > 0 ? (
          Object.entries(groupedSuggestions).map(([styleName, items], groupIndex) => (
            <div key={groupIndex} style={{ width: "100%" }}>
              <Box borderRadius="md">
                {/* Group Header */}
                <HStack
                  backgroundColor="#F5F5F5"
                  justifyContent="space-between"
                  alignItems="center"
                  borderBottom="1px solid #e2e8f0"
                  px="4"
                  py="3"
                  cursor="pointer"
                  onClick={() => toggleGroup(styleName)}
                  _hover={{ backgroundColor: "#EEEEEE" }}
                >
                  <HStack gap="2" alignItems="center">
                    {openGroups[styleName] ? (
                      <ChevronDownIcon w={6} h={6} color="black" />
                    ) : (
                      <ChevronRightIcon w={6} h={6} color="black" />
                    )}
                    <Text fontSize="17px" fontWeight="medium" color="black">
                      {styleName}
                    </Text>
                    {/* <Text fontSize="14px" color="gray.600" fontWeight="normal">
                      ({items.length} {items.length === 1 ? 'suggestion' : 'suggestions'})
                    </Text> */}
                    <Text fontSize="14px" color="gray.600" fontWeight="normal">
                      ({items.length})
                    </Text>
                  </HStack>

                  <HStack gap="8px" paddingRight="20px" onClick={(e) => e.stopPropagation()}>
                    <Button
                      display="flex"
                      gap="8px"
                      borderRadius="8px"
                      padding="10px 12px"
                      variant="outline"
                      backgroundColor="white"
                      border="1px solid #E0DBD7"
                      size="sm"
                      color="#504C4A"
                      onClick={(e) => {
                        e.stopPropagation();
                        acceptAllSuggestions(items);
                      }}
                      _hover={{ backgroundColor: "#f0f0f0" }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M13.3337 4L6.00033 11.3333L2.66699 8"
                          stroke="#208923"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Accept All
                    </Button>
                    <Button
                      variant="outline"
                      display="flex"
                      gap="8px"
                      borderRadius="8px"
                      padding="10px 12px"
                      backgroundColor="white"
                      border="1px solid #E0DBD7"
                      size="sm"
                      color="#504C4A"
                      onClick={(e) => {
                        e.stopPropagation();
                        rejectAllSuggestions(items);
                      }}
                      _hover={{ backgroundColor: "#f0f0f0" }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M12 4L4 12"
                          stroke="#E30F0F"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4 4L12 12"
                          stroke="#E30F0F"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Reject All
                    </Button>
                  </HStack>
                </HStack>

                {/* Individual Suggestions */}
                {openGroups[styleName] &&
                  items.map((item, i) => (
                    <Box
                      key={i}
                      p="4"
                      paddingRight="32px"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap="20px"
                      cursor="pointer"
                      onClick={() => moveToPosition(item)}
                      style={{ borderBottom: "1px solid #eee" }}
                      _hover={{ backgroundColor: "#f9f9f9" }}
                    >
                      <Box width="calc(100% - 130px)">
                        <Heading
                          fontSize="15px"
                          display="flex"
                          gap="4px"
                          color="#333333"
                          width="100%"
                          whiteSpace="pre-wrap"
                        >
                          <span style={{ color: "#E30F0F", fontWeight: "600" }}>Original: </span>
                          <span style={{ wordBreak: "break-all", fontWeight: "600" }}>
                            {item.originalText === " "
                              ? "<space>"
                              : item.originalText.replace(/\n/g, "\\n")}
                          </span>
                        </Heading>

                        {item.suggestionText && item.suggestionText.trim() && (
                          <Heading
                            fontSize="15px"
                            color="#333333"
                            mt="9px"
                            gap="4px"
                            width="100%"
                            display="flex"
                            whiteSpace="pre-wrap"
                          >
                            <span style={{ color: "#208923", fontWeight: "600" }}>Suggested: </span>
                            <span style={{ wordBreak: "break-all", fontWeight: "600" }}>
                              {item.suggestionText === " "
                                ? "<space>"
                                : item.suggestionText.replace(/\n/g, "\\n")}
                            </span>
                          </Heading>
                        )}

                        <Text fontSize="12px" color="gray.500" mt="6px">
                          Line {item.startAnchor.row + 1}, Column {item.startAnchor.column + 1}
                        </Text>
                      </Box>

                      <HStack
                        minWidth="130px"
                        justifyContent="flex-end"
                        gap="6px"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          width="auto"
                          height="fit-content"
                          border="1px solid #DDDDDD"
                          borderRadius="20px"
                          padding="7px"
                          onClick={(e) => {
                            e.stopPropagation();
                            acceptSuggestion(item);
                          }}
                          _hover={{ backgroundColor: "#e8f5e9" }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M13.3337 4L6.00033 11.3333L2.66699 8"
                              stroke="#208923"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          width="auto"
                          height="fit-content"
                          border="1px solid #DDDDDD"
                          borderRadius="20px"
                          padding="7px"
                          onClick={(e) => {
                            e.stopPropagation();
                            rejectSuggestion(item);
                          }}
                          _hover={{ backgroundColor: "#ffebee" }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                              d="M12 4L4 12"
                              stroke="#E30F0F"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M4 4L12 12"
                              stroke="#E30F0F"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          width="auto"
                          height="fit-content"
                          border="1px solid #3498db"
                          borderRadius="20px"
                          padding="7px"
                          color="#3498db"
                          onClick={(e) => {
                            e.stopPropagation();
                            insertQuery(item);
                          }}
                          _hover={{ backgroundColor: "#e3f2fd" }}
                        >
                          {/* <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 19H11V17H13V19ZM15.07 11.25L14.17 12.17C13.45 12.9 13 13.5 13 15H11V14.5C11 13.4 11.45 12.4 12.17 11.67L13.41 10.41C13.78 10.05 14 9.55 14 9C14 7.9 13.1 7 12 7C10.9 7 10 7.9 10 9H8C8 6.79 9.79 5 12 5C14.21 5 16 6.79 16 9C16 9.88 15.64 10.68 15.07 11.25Z"
                              fill="#3498db"
                            />
                          </svg> */}
                          query
                        </Button>
                      </HStack>
                    </Box>
                  ))}
              </Box>
            </div>
          ))
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
            flexDirection="column"
            gap="8px"
          >
            <Text fontSize="20px" fontWeight="500" color="rgba(0, 0, 0, 0.6)">
              No Suggestions
            </Text>
            <Text fontSize="14px" color="rgba(0, 0, 0, 0.4)">
              Suggestions will appear here when available
            </Text>
          </Box>
        )}
      </VStack>
    </>
  );
};

const mapStateToProps = (state, ownProps) => ({
  autoSuggestions: state.autoSuggestion.suggestions,
  autoSuggestionsRejected: state.editToolRejected.autoSuggestionReject,
  ace: ownProps.ace,
  setTextFile: ownProps.setTextFile,
  pdfFull: ownProps.pdfFull,
  toggleEditor: ownProps.toggleEditor,
});

const mapDispatchToProps = (dispatch) => {
  return {
    addAutoSuggestion: (data) => dispatch({ type: "addAutoSuggestion", payload: data }),
    removeAutoSuggestion: (data) => dispatch({ type: "removeAutoSuggestion", payload: data }),
    clearAutoSuggestions: () => dispatch({ type: "clearAutoSuggestion" }),
    addAutoSuggestionReject: (data) => dispatch({ type: "AddAutoSuggestionReject", payload: data }),
    clearAutoSuggestionRejects: () => dispatch({ type: "EmptyAutoSuggestionReject" }),
    editToolAction: (data) => dispatch({ type: "EDITTOOL", payload: data }),
    toolStyles: (data) => dispatch({ type: "ToolSubmit", payload: data }),
    editToolRejected: () => dispatch({ type: "EmptyReject", payload: [] }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(EditCorrections);